import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import { toast } from 'sonner';
import { safeFormat } from '../lib/utils';

const LeavesPage = ({ user, onLogout }) => {
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'Paid Leave',
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeaves();
    fetchLeaveBalance();
  }, []);

  const fetchLeaves = async () => {
    try {
      const response = await api.get('/leaves/my-leaves');
      setLeaves(response.data);
    } catch (error) {
      console.error('Failed to fetch leaves');
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get('/leaves/balance');
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balance');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/leaves/apply', formData);
      toast.success('Leave application submitted successfully!');
      setShowForm(false);
      setFormData({
        leave_type: 'Paid Leave',
        start_date: '',
        end_date: '',
        reason: '',
      });
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave application');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-800';
      case 'rejected':
        return 'bg-rose-100 text-rose-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={onLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">
                Leave Management
              </h1>
              <p className="text-lg text-muted-foreground">Apply and track your leave requests</p>
            </div>
            <Button data-testid="apply-leave-button" onClick={() => setShowForm(!showForm)}>
              <Plus className="mr-2 h-4 w-4" />
              {showForm ? 'Cancel' : 'Apply Leave'}
            </Button>
          </div>

          {/* Leave Balance Section - Show only when form is NOT open */}
          {!showForm && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">Total Paid Leaves</h3>
                  <Calendar className="h-5 w-5 text-accent" />
                </div>
                <p className={`text-4xl font-bold ${(leaveBalance?.total_leaves || 0) < 0 ? 'text-red-600' : ''}`} data-testid="leaves-total">
                  {leaveBalance?.total_leaves || 0}
                </p>
                {(leaveBalance?.total_leaves || 0) < 0 && (
                  <p className="text-xs text-red-500 mt-1">Deficit from overtime deductions</p>
                )}
              </div>

              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">Used Leaves</h3>
                  <CalendarDays className="h-5 w-5 text-warning" />
                </div>
                <p className="text-4xl font-bold text-warning" data-testid="leaves-used">
                  {leaveBalance?.used_leaves || 0}
                </p>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground">Remaining Leaves</h3>
                  <Calendar className="h-5 w-5 text-success" />
                </div>
                <p className={`text-4xl font-bold ${(leaveBalance?.remaining_leaves || 0) < 0 ? 'text-red-600' : 'text-success'}`} data-testid="leaves-remaining">
                  {leaveBalance?.remaining_leaves || 0}
                </p>
                {(leaveBalance?.remaining_leaves || 0) < 0 && (
                  <p className="text-xs text-red-500 mt-1">Overtime deficit - future leaves will cover this</p>
                )}
              </div>
            </div>
          )}

          {/* Apply Leave Form - Show only when button is clicked */}
          {showForm && (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
              <h2 className="text-xl font-semibold font-heading tracking-tight mb-6">
                Apply for Leave
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="leave_type">Leave Type <span className="text-destructive">*</span></Label>
                    <Select
                      value={formData.leave_type}
                      onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                      required
                    >
                      <SelectTrigger data-testid="leave-type-select">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Paid Leave">Paid Leave</SelectItem>
                        <SelectItem value="Sick Leave">Sick Leave</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date <span className="text-destructive">*</span></Label>
                    <Input
                      id="start_date"
                      data-testid="start-date-input"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date <span className="text-destructive">*</span></Label>
                    <Input
                      id="end_date"
                      data-testid="end-date-input"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason <span className="text-destructive">*</span></Label>
                  <Textarea
                    id="reason"
                    data-testid="leave-reason-input"
                    placeholder="Enter reason for leave"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={4}
                    required
                  />
                </div>

                <Button type="submit" data-testid="submit-leave-button" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Leave Application'}
                </Button>
              </form>
            </div>
          )}

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-xl font-semibold font-heading tracking-tight mb-6">
              Leave History
            </h2>
            <div className="space-y-4" data-testid="leave-history-list">
              {leaves.map((leave) => (
                <div
                  key={leave.leave_id}
                  className="border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{leave.leave_type}</h3>
                      <p className="text-sm text-muted-foreground">
                        {leave.start_date} to {leave.end_date}
                      </p>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(leave.status)}`}>
                      {leave.status}
                    </span>
                  </div>
                  <p className="text-sm mt-2">{leave.reason}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Applied on: {safeFormat(leave.applied_at, 'MMM d, yyyy')}
                  </p>
                </div>
              ))}
              {leaves.length === 0 && (
                <div className="text-center py-12">
                  <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No leave applications yet</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold font-heading tracking-tight mb-4">
                Approved Leaves
              </h2>
              <div className="space-y-3" data-testid="approved-leaves-list">
                {leaves.filter(l => l.status === 'approved').map((leave) => (
                  <div key={leave.leave_id} className="border-l-4 border-success pl-4 py-2">
                    <p className="font-semibold">{leave.leave_type}</p>
                    <p className="text-sm text-muted-foreground">
                      {leave.start_date} to {leave.end_date}
                    </p>
                  </div>
                ))}
                {leaves.filter(l => l.status === 'approved').length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No approved leaves</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold font-heading tracking-tight mb-4">
                Pending Leaves
              </h2>
              <div className="space-y-3" data-testid="pending-leaves-list">
                {leaves.filter(l => l.status === 'pending').map((leave) => (
                  <div key={leave.leave_id} className="border-l-4 border-warning pl-4 py-2">
                    <p className="font-semibold">{leave.leave_type}</p>
                    <p className="text-sm text-muted-foreground">
                      {leave.start_date} to {leave.end_date}
                    </p>
                  </div>
                ))}
                {leaves.filter(l => l.status === 'pending').length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No pending leaves</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default LeavesPage;